"use client";

import { useCallback, useEffect, useRef, useState } from "react";
import { Mail, Loader2, RefreshCw } from "lucide-react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { publicErrorMessage } from "@ocr-monorepo/shared";
import { Button } from "@ocr-monorepo/ui/components/button";
import { Input } from "@ocr-monorepo/ui/components/input";
import { useTRPC } from "@/lib/trpc";
import { captureGrowthEvent } from "@/lib/analytics";
import { client as authClient } from "@/lib/auth-client";

interface EmailVerificationStepProps {
  email: string;
  onNext?: () => void;
}

const RESEND_DELAY_SECONDS = 60;

export function EmailVerificationStep({
  email,
  onNext,
}: EmailVerificationStepProps) {
  const trpc = useTRPC();
  const queryClient = useQueryClient();
  const [overriddenEmail, setOverriddenEmail] = useState<string | null>(null);
  const [newEmail, setNewEmail] = useState("");
  const [editingEmail, setEditingEmail] = useState(false);
  const [changingEmail, setChangingEmail] = useState(false);
  const [checking, setChecking] = useState(false);
  const [resendAvailableAt, setResendAvailableAt] = useState(
    () => Date.now() + RESEND_DELAY_SECONDS * 1000,
  );
  const [countdown, setCountdown] = useState(RESEND_DELAY_SECONDS);
  const verificationCapturedRef = useRef(false);

  const displayEmail = overriddenEmail ?? email;

  const completeVerification = useCallback(
    (verificationMethod: "poll" | "manual") => {
      if (verificationCapturedRef.current) return;
      verificationCapturedRef.current = true;
      captureGrowthEvent("onboarding_step_completed", {
        step: "verify_email",
        verification_method: verificationMethod,
      });
      onNext?.();
    },
    [onNext],
  );

  const { data: verificationStatus } = useQuery({
    ...trpc.onboarding.checkEmailVerification.queryOptions(),
    refetchInterval: (query) =>
      query.state.data?.emailVerified ? false : 10000,
    staleTime: 5000,
  });

  const resendEmailMutation = useMutation(
    trpc.user.sendEmailVerification.mutationOptions(),
  );

  useEffect(() => {
    const updateCountdown = () => {
      setCountdown(
        Math.max(0, Math.ceil((resendAvailableAt - Date.now()) / 1000)),
      );
    };
    updateCountdown();
    const interval = window.setInterval(updateCountdown, 1000);
    return () => window.clearInterval(interval);
  }, [resendAvailableAt]);

  useEffect(() => {
    if (!verificationStatus?.emailVerified) return;
    const timeout = window.setTimeout(() => completeVerification("poll"), 600);
    return () => window.clearTimeout(timeout);
  }, [verificationStatus?.emailVerified, completeVerification]);

  const restartCooldown = () => {
    setResendAvailableAt(Date.now() + RESEND_DELAY_SECONDS * 1000);
  };

  const handleResend = async () => {
    if (countdown > 0) return;
    try {
      await resendEmailMutation.mutateAsync();
      restartCooldown();
      toast.success("Verification email sent. Check your inbox.");
    } catch {
      toast.error("Failed to resend email", {
        description: publicErrorMessage("system_error"),
      });
    }
  };

  const handleChangeEmail = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const normalizedEmail = newEmail.trim().toLowerCase();
    if (!normalizedEmail || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(normalizedEmail)) {
      toast.error("Enter a valid email address.");
      return;
    }
    try {
      setChangingEmail(true);
      const result = await authClient.changeEmail({
        newEmail: normalizedEmail,
        callbackURL: `${window.location.origin}/getting-started`,
      });
      if (result.error) throw new Error(result.error.message);
      setOverriddenEmail(normalizedEmail);
      setEditingEmail(false);
      setNewEmail("");
      restartCooldown();
      toast.success("Email updated. Check the new address for a link.");
      captureGrowthEvent("onboarding_verification_email_changed");
    } catch {
      toast.error("We couldn't update your email. Please try again.");
    } finally {
      setChangingEmail(false);
    }
  };

  const handleVerified = async () => {
    setChecking(true);
    try {
      await queryClient.invalidateQueries({
        queryKey: trpc.onboarding.checkEmailVerification.queryKey(),
      });
      const status = await queryClient.fetchQuery(
        trpc.onboarding.checkEmailVerification.queryOptions(),
      );
      if (status.emailVerified) {
        completeVerification("manual");
      } else {
        captureGrowthEvent("onboarding_email_verification_failed", {
          reason: "not_verified",
        });
        toast.error("Email not verified yet", {
          description: "Open the link in your email, then check again.",
        });
      }
    } catch {
      captureGrowthEvent("onboarding_email_verification_failed", {
        reason: "status_check_failed",
      });
      toast.error("Couldn't check verification. Please try again.");
    } finally {
      setChecking(false);
    }
  };

  return (
    <div className="mx-auto w-full max-w-[640px] space-y-[18px]">
      <p className="text-[13px] leading-5 text-muted-foreground md:hidden">
        Step 2 of 3 · Verify email
      </p>

      <div className="space-y-3 md:text-center">
        <div className="flex size-12 items-center justify-center rounded-md border border-border bg-card md:mx-auto">
          <Mail className="size-6 text-primary" aria-hidden="true" />
        </div>
        <h1 className="text-[28px] font-medium leading-[34px] tracking-tight md:text-[30px] md:leading-9">
          Check your email
        </h1>
        <div className="space-y-1">
          <p className="text-base leading-6 text-muted-foreground">
            We sent a verification link to
          </p>
          <p className="break-all text-base font-medium leading-6" suppressHydrationWarning>
            {displayEmail || "your email address"}
          </p>
        </div>
      </div>

      <div className="flex items-start gap-3 rounded-md border border-border bg-secondary p-4 text-sm leading-[22px]">
        <RefreshCw className="mt-0.5 size-[18px] shrink-0 text-primary" aria-hidden="true" />
        <p>Open the link in your email. This page will continue automatically.</p>
      </div>

      <div className="space-y-3">
        <Button
          type="button"
          variant="outline"
          onClick={() => void handleVerified()}
          className="h-12 w-full rounded-md bg-card text-[15px]"
          disabled={checking || verificationStatus?.emailVerified}
          data-email-verify
        >
          {checking ? (
            <>
              <Loader2 className="size-4 animate-spin" aria-hidden="true" />
              Checking…
            </>
          ) : verificationStatus?.emailVerified ? (
            "Email verified"
          ) : (
            "Check verification"
          )}
        </Button>
        <p className="text-[13px] leading-5 text-muted-foreground">
          The link expires in 24 hours.
        </p>
      </div>

      <div className="space-y-2.5 border-t border-border pt-[18px]">
        <p className="text-sm leading-[22px]">No email? Check your spam folder.</p>
        <div className="flex flex-wrap items-center justify-between gap-2">
          <Button
            type="button"
            variant="link"
            onClick={() => void handleResend()}
            disabled={countdown > 0 || resendEmailMutation.isPending}
            className="h-auto p-0 text-sm"
            suppressHydrationWarning
          >
            {resendEmailMutation.isPending
              ? "Sending…"
              : countdown > 0
                ? `Resend in ${countdown}s`
                : "Resend email"}
          </Button>
          <Button
            type="button"
            variant="link"
            onClick={() => setEditingEmail((value) => !value)}
            className="h-auto p-0 text-sm"
          >
            Use a different email
          </Button>
        </div>

        {editingEmail && (
          <form onSubmit={handleChangeEmail} className="space-y-2 pt-2">
            <label htmlFor="new-verification-email" className="text-sm font-medium">
              New email address
            </label>
            <Input
              id="new-verification-email"
              type="email"
              inputMode="email"
              autoComplete="email"
              value={newEmail}
              onChange={(event) => setNewEmail(event.target.value)}
              className="h-12 rounded-md bg-card px-3.5 text-base md:text-base"
              required
            />
            <Button
              type="submit"
              className="h-12 w-full rounded-md"
              disabled={changingEmail}
            >
              {changingEmail ? "Updating…" : "Update and resend"}
            </Button>
          </form>
        )}
      </div>
    </div>
  );
}
